import { useEffect } from 'react';

import { uuid } from '@atlaskit/adf-schema';
import { fg } from '@atlaskit/platform-feature-flags';

import type {
	AiStreamingOrchestratorPlugin,
	StreamingError,
} from './aiStreamingOrchestratorPluginType';
import { fireADFStreamingEndSessionErrorAnalytics } from './editor-actions/analytics';
import { createStreamFlowPacer } from './editor-actions/stream-flow-pacer';
import { StreamCommandQueue } from './editor-actions/streamCommandQueue';

export const aiStreamingOrchestratorPlugin: AiStreamingOrchestratorPlugin = ({ api, config }) => {
	if (!api) {
		throw new Error('api is not defined');
	}

	let streamQueue: StreamCommandQueue | undefined;

	// Only allow one session to be active at a time
	let sessionId: string | null = null;
	// Tracking variables for debugging session lifecycle issues
	let hasSessionBeenStarted = false;
	let isEndingSession = false;
	let lastEndedSessionId: string | null = null;
	let endSessionCallCount = 0;
	let sessionAborted = false;
	const streamFlowPacer = createStreamFlowPacer(config?.streamFlowPacerEnabled);

	return {
		name: 'aiStreamingOrchestrator',
		actions: {
			startSession: (traceId?: string, params?: { singleInstrumentationId?: string }) => {
				sessionId = fg('platform_editor_ai_aifc_patch_ga_blockers')
					? (crypto.randomUUID() ?? uuid.generate())
					: crypto.randomUUID();
				hasSessionBeenStarted = true;
				sessionAborted = false;
				streamQueue = new StreamCommandQueue(api, streamFlowPacer);
				if (fg('platform_editor_ai_traceid_in_analytics_events')) {
					streamQueue.startSession(sessionId, traceId, params?.singleInstrumentationId);
					api.aiExperience.actions.startSession(sessionId, traceId);
				} else {
					streamQueue.startSession(sessionId, undefined, params?.singleInstrumentationId);
					api.aiExperience.actions.startSession(sessionId);
				}
				return sessionId;
			},

			endSession: async (error?: StreamingError) => {
				// In the session abort we end the session and no longer need to run this
				// On a new session we reset the state so this is safe to ignore
				if (sessionAborted) {
					return;
				}
				if (sessionId === null) {
					if (fg('platform_editor_ai_aifc_patch_ga_blockers')) {
						fireADFStreamingEndSessionErrorAnalytics({
							api,
							sessionStarted: hasSessionBeenStarted,
							endSessionCallCount,
							lastEndedSessionId,
							isDoubleCall: isEndingSession,
						});
						throw new Error(`endSession requires a session to be started first!`);
					} else {
						throw new Error(`endSession requires a session to be started first!`);
					}
				}

				endSessionCallCount++;
				isEndingSession = true;
				const currentSessionId = sessionId;

				try {
					streamQueue?.queueEndSession(currentSessionId);
					if (error) {
						if (fg('platform_editor_ai_migrate_from_xstate')) {
							api.aiExperience.actions.dispatchAIEvent({
								type: 'error',
								errorInfo: {
									failureReason: error.failureReason,
									statusCode: error.statusCode,
								},
							});
						} else {
							const { send } = api.aiExperience.actions.getAiExperienceActor();
							send({
								type: 'error',
								errorInfo: {
									failureReason: error.failureReason,
									statusCode: error.statusCode,
								},
							});
						}
					}
					// Since `endSession` is going to be called after all the actions are invoked
					// so we can basically wait for the queue to be empty
					await streamQueue?.onEmptyQueue();
					streamQueue?.endSession(currentSessionId);
					// https://atlassian.slack.com/archives/C0962DYFLRL/p1759397599541949?thread_ts=1759366160.706849&cid=C0962DYFLRL
					// streamQueue?.destroy();
					api.aiExperience.actions.endSession(currentSessionId, {
						type: error ? 'error' : 'finished',
					});
					hasSessionBeenStarted = false;
					lastEndedSessionId = currentSessionId;
					sessionId = null;
				} finally {
					isEndingSession = false;
				}
			},

			abortSession: () => {
				// abortSession can be called multiple times from multiple command handlers
				if (!sessionId) {
					return;
				}

				const sessionToEnd = sessionId;
				lastEndedSessionId = sessionToEnd;
				sessionId = null;
				streamQueue?.abort();
				if (sessionToEnd) {
					api.aiExperience.actions.endSession(sessionToEnd, { type: 'abort' });
					sessionAborted = true;
				}
			},

			initAction: (command, options) => {
				if (sessionId === null) {
					throw new Error('initAction requires a session to be started first!');
				}
				const actionId = crypto.randomUUID();
				streamQueue?.initStreamCommand(actionId, command, options);
				return actionId;
			},

			// We are sending the full document as a chunk for create actions
			streamValue: (actionId: string, chunk: string) => {
				// Use the queue manager to handle streaming
				// This will either process immediately if actor is ready, or queue for later
				streamQueue?.queueStreamCommand(actionId, chunk);
			},

			finishAction: (actionId: string, error?: StreamingError) => {
				if (error) {
					if (fg('platform_editor_ai_migrate_from_xstate')) {
						api.aiExperience.actions.dispatchAIEvent({
							type: 'error',
							errorInfo: {
								failureReason: error.failureReason,
								statusCode: error.statusCode,
							},
						});
					} else {
						const { send } = api.aiExperience.actions.getAiExperienceActor();
						send({
							type: 'error',
							errorInfo: {
								failureReason: error.failureReason,
								statusCode: error.statusCode,
							},
						});
					}
				}
				streamQueue?.endStreamCommand(actionId);
			},
		},
		usePluginHook: () => {
			useEffect(() => {
				return () => {
					streamQueue?.destroy();
				};
			}, []);
		},
	};
};
